if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.pointerInside = false;
        this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);
        this.onPointerEnter = () => {
          this.pointerInside = true;
        };
        this.onPointerLeave = () => {
          this.pointerInside = false;
        };

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
        this.addEventListener('pointerenter', this.onPointerEnter);
        this.addEventListener('pointerleave', this.onPointerLeave);
        this.addEventListener('click', this.onGalleryClick.bind(this));
        document.addEventListener('keydown', this.onDocumentKeyDown);
      }

      disconnectedCallback() {
        this.removeEventListener('pointerenter', this.onPointerEnter);
        this.removeEventListener('pointerleave', this.onPointerLeave);
        document.removeEventListener('keydown', this.onDocumentKeyDown);
      }

      onGalleryClick() {
        if (!this.mql.matches) return;
        if (this.contains(document.activeElement)) return;
        this.focus({ preventScroll: true });
      }

      isTypingTarget(element) {
        if (!element || element === document.body) return false;
        const tag = element.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
        if (element.isContentEditable) return true;
        return Boolean(element.closest('input, textarea, select, [contenteditable="true"]'));
      }

      isGallerySelected() {
        if (this.pointerInside) return true;
        return this.contains(document.activeElement);
      }

      onDocumentKeyDown(event) {
        if (!this.mql.matches) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (event.metaKey || event.ctrlKey || event.altKey) return;
        if (this.isTypingTarget(event.target) || this.isTypingTarget(document.activeElement)) return;
        if (!this.isGallerySelected()) return;
        if (document.querySelector('product-modal[open], details[open] .modal-video, .product-popup-modal[open]')) return;

        const mediaIds = this.getNavigableMediaIds();
        if (mediaIds.length < 2) return;

        const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        let index = activeMedia ? mediaIds.indexOf(activeMedia.dataset.mediaId) : 0;
        if (index === -1) index = 0;

        const nextIndex =
          event.key === 'ArrowRight'
            ? (index + 1) % mediaIds.length
            : (index - 1 + mediaIds.length) % mediaIds.length;

        event.preventDefault();
        this.setActiveMedia(mediaIds[nextIndex], false);
      }

      getNavigableMediaIds() {
        if (this.elements.thumbnails) {
          return Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'))
            .filter((item) => {
              const style = window.getComputedStyle(item);
              return style.display !== 'none' && style.visibility !== 'hidden';
            })
            .map((item) => item.dataset.target);
        }

        return Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'))
          .filter((item) => {
            if (item.classList.contains('product__media-item--variant')) {
              return item === item.parentElement.querySelector('.product__media-item--variant');
            }
            return true;
          })
          .map((item) => item.dataset.mediaId);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          // window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
